import {
  CandidateRgpdStatsDTO,
  Exam,
  Page,
  PageRequest,
  ExamResult,
} from '$synapse/models';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { genericBaseUrl } from './utils';
import { ToastrService } from 'ngx-toastr';
import { CreateExamDTO, ExamDTO, mapToExam } from '$synapse/dtos';

@Injectable({
  providedIn: 'root',
})
export class ExamService {
  private readonly _http = inject(HttpClient);
  private readonly _toastr = inject(ToastrService);

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  baseUrl(...path: any[]): string {
    return genericBaseUrl(`${environment.settings.api.url}/exams`, ...path);
  }

  managerBaseUrl(...path: any[]): string {
    return genericBaseUrl(
      `${environment.settings.api.url}/manager/exams`,
      ...path
    );
  }

  getResult(examId: string): Observable<ExamResult> {
    return this._http.get<ExamResult>(this.baseUrl(examId, 'results'));
  }

  getExam(examId: string): Observable<Exam> {
    return this._http.get<ExamDTO>(this.baseUrl(examId)).pipe(map(mapToExam));
  }

  deleteOne(id: string): Observable<void> {
    return this._http.delete<void>(this.managerBaseUrl(id));
  }

  addOne(createExamDTO: CreateExamDTO): Observable<Exam> {
    return this._http
      .post<ExamDTO>(this.managerBaseUrl(), createExamDTO, this.httpOptions)
      .pipe(map(mapToExam));
  }

  sendReminder(exam: Exam): Observable<object> {
    return this._http.post(
      this.managerBaseUrl('remind', exam.uuid),
      this.httpOptions
    );
  }

  findAllResults(page: PageRequest): Observable<Page<CandidateRgpdStatsDTO>> {
    return this._http
      .get<Page<CandidateRgpdStatsDTO>>(this.baseUrl('results'), {
        params: page.params(),
      })
      .pipe(
        map((response) => {
          const content = (response as any).content;
          return new Page<CandidateRgpdStatsDTO>(page, content, content.length);
        })
      );
  }

  findAllJointResults(
    groupId?: string,
    groupAssessmentId?: string
  ): Observable<Exam[]> {
    let queryParams = new HttpParams();
    if (groupId) {
      queryParams = queryParams.append('groupId', groupId);
      if (groupAssessmentId) {
        queryParams = queryParams.append(
          'groupAssessmentId',
          groupAssessmentId
        );
      }
    }
    return this._http
      .get<ExamDTO[]>(this.baseUrl('results', 'all'), {
        params: queryParams,
      })
      .pipe(map((response) => response.map(mapToExam)));
  }

  updateExam(exam: Exam, date): Observable<Exam> {
    const newDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    return this._http
      .patch<ExamDTO>(
        this.managerBaseUrl(exam.uuid),
        JSON.stringify({ expirationDate: newDate }),
        this.httpOptions
      )
      .pipe(
        map((response) => {
          this._toastr.success("Date d'expiration changée avec succès.");
          return mapToExam(response);
        }),
        catchError(() => {
          this._toastr.error(
            "Erreur lors du changement de la date d'expiration"
          );
          return of(null);
        })
      );
  }
}
